import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { GameManager, gameManager } from 'src/app/game/businesslogic/GameManager';
import { GhsManager } from 'src/app/game/businesslogic/GhsManager';
import { SettingsManager, settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { AbilityCard } from 'src/app/game/model/data/AbilityCard';
import { GameState } from 'src/app/game/model/Game';
import { Monster } from 'src/app/game/model/Monster';
import { AbilityCardComponent } from 'src/app/ui/figures/ability-card/ability-card';
import { applyPlaceholder, GhsLabelDirective } from 'src/app/ui/helper/label';
import { PointerInputDirective } from 'src/app/ui/helper/pointer-input';
import { GhsTooltipDirective } from 'src/app/ui/helper/tooltip/tooltip';
import { TrackUUIDPipe } from 'src/app/ui/helper/trackUUID';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    GhsLabelDirective,
    PointerInputDirective,
    GhsTooltipDirective,
    TrackUUIDPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    AbilityCardComponent
  ],
  selector: 'ghs-ability-deck-dialog',
  templateUrl: './ability-deck-dialog.html',
  styleUrls: ['./ability-deck-dialog.scss']
})
export class AbilityDeckDialogComponent implements OnInit {
  dialogRef = inject(DialogRef);
  private ghsManager = inject(GhsManager);

  monster: Monster = inject(DIALOG_DATA);

  reveal: number = 0;

  gameManager: GameManager = gameManager;
  settingsManager: SettingsManager = settingsManager;
  GameState = GameState;
  edit: boolean = false;
  bottomActions: boolean = false;
  abilityCards: AbilityCard[] = [];
  upcomingCards: AbilityCard[] = [];
  discardedCards: AbilityCard[] = [];
  deletedCards: AbilityCard[] = [];
  keepRevealed: boolean[] = [];

  constructor() {
    this.ghsManager.uiChangeEffect(() => this.update());
  }

  ngOnInit(): void {
    this.bottomActions = gameManager.monsterManager.hasBottomActions(this.monster);
    this.dialogRef.closed.subscribe(() => {
      this.abilityCards.forEach((abilityCard, i) => {
        if (abilityCard.revealed && !this.keepRevealed[i]) {
          abilityCard.revealed = false;
        }
      });
    });
    this.update(true);
  }

  toggleEdit() {
    this.edit = !this.edit;
  }

  update(init: boolean = false) {
    const abilityNumber = this.monster.ability;

    this.abilityCards = this.monster.abilities.map((value) => gameManager.abilityCards(this.monster)[value]);

    this.upcomingCards = this.abilityCards.filter((value, index) => index > abilityNumber);
    this.discardedCards = this.abilityCards.filter((value, index) => index <= abilityNumber).reverse();
    this.deletedCards = gameManager.deckData(this.monster).abilities.filter((abilityCard) => !this.abilityCards.includes(abilityCard));

    if (init) {
      this.keepRevealed = [...this.monster.revealedAbilities];
      this.abilityCards.forEach((abilityCard, i) => {
        abilityCard.revealed = this.keepRevealed[i];
      });
    }
  }

  abilityIndex(abilityCard: AbilityCard) {
    return gameManager.abilityCards(this.monster).indexOf(abilityCard);
  }

  shuffle(upcoming: boolean = false) {
    gameManager.stateManager.before('shuffleAbilityDeck' + (upcoming ? 'Upcoming' : ''), 'data.monster.' + this.monster.name);
    gameManager.monsterManager.shuffleAbilities(this.monster, upcoming);
    gameManager.sortFigures();
    this.keepRevealed = [];
    gameManager.stateManager.after();
    this.update();
  }

  draw() {
    gameManager.stateManager.before('drawAbility', 'data.monster.' + this.monster.name);
    gameManager.monsterManager.drawAbility(this.monster);
    gameManager.sortFigures();
    gameManager.stateManager.after();
    this.update();
  }

  toggleDrawExtra() {
    if (this.monster.drawExtra) {
      gameManager.stateManager.before('unsetDrawExtraAbility', 'data.monster.' + this.monster.name);
      this.monster.drawExtra = false;
      gameManager.monsterManager.applySameDeck(this.monster);
      gameManager.stateManager.after();
    } else if (gameManager.monsterManager.applySameDeck(this.monster)) {
      gameManager.stateManager.before('setDrawExtraAbility', 'data.monster.' + this.monster.name);
      this.monster.drawExtra = true;
      if (gameManager.game.state === GameState.next) {
        gameManager.monsterManager.drawExtra(this.monster);
      }
      gameManager.sortFigures();
      gameManager.stateManager.after();
    }
    this.update();
  }

  dropUpcoming(event: CdkDragDrop<AbilityCard[]>) {
    gameManager.stateManager.before('reorderAbilities', 'data.monster.' + this.monster.name);
    if (event.container === event.previousContainer) {
      const offset = this.monster.ability + 1;
      moveItemInArray(this.monster.abilities, event.previousIndex + offset, event.currentIndex + offset);
      moveItemInArray(this.monster.revealedAbilities, event.previousIndex + offset, event.currentIndex + offset);
    } else {
      const offset = this.monster.ability;
      moveItemInArray(this.monster.abilities, offset - event.previousIndex, event.currentIndex + offset);
      moveItemInArray(this.monster.revealedAbilities, offset - event.previousIndex, event.currentIndex + offset);
      this.monster.ability = this.monster.ability - 1;
    }
    const sameDeckMonster = gameManager.monsterManager.getSameDeckMonster(this.monster);
    if (sameDeckMonster) {
      gameManager.monsterManager.applySameDeck(sameDeckMonster);
    }
    gameManager.stateManager.after();
    this.update();
    this.keepRevealed = [...this.monster.revealedAbilities];
  }

  dropDiscarded(event: CdkDragDrop<AbilityCard[]>) {
    gameManager.stateManager.before('reorderAbilities', 'data.monster.' + this.monster.name);
    if (event.container === event.previousContainer) {
      moveItemInArray(this.monster.abilities, this.monster.ability - event.previousIndex, this.monster.ability - event.currentIndex);
      moveItemInArray(
        this.monster.revealedAbilities,
        this.monster.ability - event.previousIndex,
        this.monster.ability - event.currentIndex
      );
    } else {
      this.monster.ability = this.monster.ability + 1;
      const offset = this.monster.ability;
      moveItemInArray(this.monster.abilities, event.previousIndex + offset, offset - event.currentIndex);
      moveItemInArray(this.monster.revealedAbilities, event.previousIndex + offset, offset - event.currentIndex);
    }
    const sameDeckMonster = gameManager.monsterManager.getSameDeckMonster(this.monster);
    if (sameDeckMonster) {
      gameManager.monsterManager.applySameDeck(sameDeckMonster);
    }
    gameManager.sortFigures();
    gameManager.stateManager.after();
    this.update();
    this.keepRevealed = [...this.monster.revealedAbilities];
  }

  restoreDefault(): void {
    gameManager.stateManager.before('restoreDefaultAbilities', 'data.monster.' + this.monster.name);
    gameManager.monsterManager.restoreDefaultAbilities(this.monster);
    gameManager.stateManager.after();
    this.update();
  }

  remove(index: number) {
    const abilityCard: AbilityCard = gameManager.abilityCards(this.monster)[this.monster.abilities[index]];
    gameManager.stateManager.before(
      'removeAbility',
      'data.monster.' + this.monster.name,
      abilityCard.name ? this.abilityLabel(abilityCard) : '' + abilityCard.cardId
    );
    gameManager.monsterManager.removeAbility(this.monster, index);
    gameManager.stateManager.after();
    this.update();
  }

  restore(abilityCard: AbilityCard) {
    gameManager.stateManager.before(
      'restoreAbility',
      'data.monster.' + this.monster.name,
      abilityCard.name ? this.abilityLabel(abilityCard) : '' + abilityCard.cardId
    );
    gameManager.monsterManager.restoreAbility(this.monster, abilityCard);
    gameManager.stateManager.after();
    this.update();
  }

  toggleKeepRevealed(index: number) {
    gameManager.stateManager.before('toggleKeepRevelead', 'data.monster.' + this.monster.name, index);
    this.keepRevealed[index] = !this.keepRevealed[index];
    if (this.keepRevealed[index] && !!this.abilityCards[index]) {
      this.abilityCards[index].revealed = true;
    }
    this.monster.revealedAbilities = [...this.keepRevealed];
    gameManager.stateManager.after();
  }

  onRevealedChange(index: number, revealed: boolean) {
    if (!revealed) {
      if (index !== -1 && this.keepRevealed[index]) {
        this.toggleKeepRevealed(index);
      }
    }
  }

  abilityLabel(abilityCard: AbilityCard): string {
    let label = 'data.monster.' + this.monster.name;
    if (abilityCard && abilityCard.name) {
      label = 'data.ability.' + abilityCard.name;
    } else if (this.monster.deck !== this.monster.name) {
      label = 'data.deck.' + this.monster.deck;
      if (label.split('.')[label.split('.').length - 1] === applyPlaceholder(settingsManager.getLabel(label)) && this.monster.deck) {
        label = 'data.monster.' + this.monster.deck;
      }
    }

    return applyPlaceholder(settingsManager.getLabel(label));
  }

  defaultSort() {
    this.monster.abilities = this.monster.abilities.sort((a, b) => a - b);
    this.update();
  }
}
